import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  type ChartOptions,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { X } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCurrency } from '@/hooks/useCurrency';
import { formatUiDate, formatUiNumber } from '@/utils/uiLocale';
import type { ForecastReviewChartResponse } from '@/services/forecastReviewService';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler);

interface ForecastLineChartProps {
  data: ForecastReviewChartResponse | null;
  loading?: boolean;
  viewType: 'units' | 'value';
  granularity: 'week' | 'month';
  onViewTypeChange: (v: 'units' | 'value') => void;
  onGranularityChange: (g: 'week' | 'month') => void;
  activeFilters?: Array<{ id: string; label: string; value: string }>;
  onClearFilter?: (filterId: string) => void;
  onClearAllFilters?: () => void;
}

function sortPeriods(periods: string[], granularity: 'week' | 'month'): string[] {
  return [...periods].sort((a, b) => {
    if (granularity === 'week') {
      const [ay, aw] = a.split('-W');
      const [by, bw] = b.split('-W');
      const cmp = Number(ay) - Number(by);
      return cmp !== 0 ? cmp : Number(aw) - Number(bw);
    }
    return a.localeCompare(b);
  });
}

function formatPeriod(period: string, granularity: 'week' | 'month', language?: string | null): string {
  if (granularity === 'week' && period.includes('-W')) {
    const [, week] = period.split('-W');
    return `S${week}`;
  }
  const [year, month] = period.split('-');
  if (!month) return period;
  const d = new Date(Number(year), Number(month) - 1, 1);
  return formatUiDate(d, { month: 'short', year: '2-digit' }, language);
}

export function ForecastLineChart({
  data,
  loading,
  viewType,
  granularity,
  onViewTypeChange,
  onGranularityChange,
  activeFilters = [],
  onClearFilter,
  onClearAllFilters,
}: ForecastLineChartProps) {
  const { t, i18n } = useTranslation();
  const { formatViewAmount, symbol } = useCurrency();
  const [showLastYear, setShowLastYear] = useState(false);
  const hasActiveFilters = activeFilters.length > 0;

  const { labels, historicalValues, forecastValues, lastYearValues, sortedPeriods } = useMemo(() => {
    if (!data) {
      return {
        labels: [] as string[],
        historicalValues: [] as (number | null)[],
        forecastValues: [] as (number | null)[],
        lastYearValues: [] as (number | null)[],
        sortedPeriods: [] as string[],
      };
    }

    const periodMap = new Map<string, { historical: number | null; forecast: number | null; lastYear: number | null }>();

    for (const p of data.historical_sales) {
      periodMap.set(p.period, { historical: p.value, forecast: null, lastYear: null });
    }
    for (const p of data.forecast) {
      const existing = periodMap.get(p.period) ?? { historical: null, forecast: null, lastYear: null };
      existing.forecast = p.value;
      periodMap.set(p.period, existing);
    }
    for (const p of data.last_year_sales ?? []) {
      const existing = periodMap.get(p.period) ?? { historical: null, forecast: null, lastYear: null };
      existing.lastYear = p.value;
      periodMap.set(p.period, existing);
    }

    const periods =
      data.periods?.length && data.periods.length > 0
        ? sortPeriods(data.periods, granularity)
        : sortPeriods([...periodMap.keys()], granularity);
    return {
      labels: periods.map((p) => formatPeriod(p, granularity, i18n.language)),
      sortedPeriods: periods,
      historicalValues: periods.map((p) => {
        if (data.current_period && p > data.current_period) return null;
        return periodMap.get(p)?.historical ?? 0;
      }),
      forecastValues: periods.map((p) => {
        if (data.current_period && p < data.current_period) return null;
        return periodMap.get(p)?.forecast ?? null;
      }),
      lastYearValues: periods.map((p) => periodMap.get(p)?.lastYear ?? 0),
    };
  }, [data, granularity, i18n.language]);

  const chartData = useMemo(() => {
    const datasets = [
      {
        label: t('forecastReview.chart.historical'),
        data: historicalValues,
        borderColor: '#1681ba',
        backgroundColor: 'rgba(22, 129, 186, 0.08)',
        borderWidth: 2,
        pointRadius: 2,
        pointHoverRadius: 4,
        tension: 0.25,
        spanGaps: false,
      },
      {
        label: t('forecastReview.chart.forecast'),
        data: forecastValues,
        borderColor: '#0f766e',
        backgroundColor: 'rgba(15, 118, 110, 0.06)',
        borderWidth: 2,
        borderDash: [6, 4],
        pointRadius: 3,
        pointHoverRadius: 5,
        tension: 0.25,
        spanGaps: true,
      },
    ];

    if (showLastYear) {
      datasets.push({
        label: t('forecastReview.chart.lastYear'),
        data: lastYearValues,
        borderColor: '#9333ea',
        backgroundColor: 'rgba(147, 51, 234, 0.06)',
        borderWidth: 2,
        borderDash: [4, 3],
        pointRadius: 2,
        pointHoverRadius: 4,
        tension: 0.25,
        spanGaps: false,
      });
    }

    return { labels, datasets };
  }, [labels, historicalValues, forecastValues, lastYearValues, showLastYear, t]);

  const isWeekly = granularity === 'week';
  const chartMinWidth = isWeekly ? Math.max(labels.length * 28, 640) : undefined;

  const options: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    plugins: {
      legend: { position: 'top', align: 'end' },
      tooltip: {
        callbacks: {
          label: (ctx) => {
            const val = ctx.parsed.y;
            if (val == null) return '';
            const formatted =
              viewType === 'value'
                ? formatViewAmount(val, viewType)
                : formatUiNumber(val, undefined, i18n.language);
            return `${ctx.dataset.label}: ${formatted}`;
          },
        },
      },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: {
          maxRotation: isWeekly ? 45 : 0,
          autoSkip: !isWeekly,
          maxTicksLimit: isWeekly ? labels.length : 14,
        },
      },
      y: {
        beginAtZero: true,
        grid: { color: 'rgba(0,0,0,0.06)' },
        ticks: {
          callback: (v) => {
            if (typeof v !== 'number') return v;
            return viewType === 'value' ? formatViewAmount(v, viewType) : formatUiNumber(v, undefined, i18n.language);
          },
        },
      },
    },
  };

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <h3 className="text-sm font-semibold text-gray-900">{t('forecastReview.chart.title')}</h3>
        <div className="flex flex-wrap items-center gap-2">
          <div className="inline-flex rounded-md border border-gray-200 p-0.5 text-xs">
            <button
              type="button"
              onClick={() => onGranularityChange('month')}
              className={`rounded px-2.5 py-1 ${granularity === 'month' ? 'bg-gray-100 font-medium text-gray-900' : 'text-gray-600'}`}
            >
              {t('forecastReview.chart.monthly')}
            </button>
            <button
              type="button"
              onClick={() => onGranularityChange('week')}
              className={`rounded px-2.5 py-1 ${granularity === 'week' ? 'bg-gray-100 font-medium text-gray-900' : 'text-gray-600'}`}
            >
              {t('forecastReview.chart.weekly')}
            </button>
          </div>
          <div className="inline-flex rounded-md border border-gray-200 p-0.5 text-xs">
            <button
              type="button"
              onClick={() => onViewTypeChange('units')}
              className={`rounded px-2.5 py-1 ${viewType === 'units' ? 'bg-gray-100 font-medium text-gray-900' : 'text-gray-600'}`}
            >
              {t('forecastReview.chart.units')}
            </button>
            <button
              type="button"
              onClick={() => onViewTypeChange('value')}
              className={`rounded px-2.5 py-1 ${viewType === 'value' ? 'bg-gray-100 font-medium text-gray-900' : 'text-gray-600'}`}
            >
              {t('forecastReview.chart.value', { currencySymbol: symbol })}
            </button>
          </div>
          <label className="inline-flex cursor-pointer items-center gap-2 rounded-md border border-gray-200 px-2.5 py-1.5 text-xs text-gray-700">
            <input
              type="checkbox"
              checked={showLastYear}
              onChange={(e) => setShowLastYear(e.target.checked)}
              className="h-3.5 w-3.5 rounded border-gray-300 text-[#1681ba] focus:ring-[#1681ba]"
            />
            {t('forecastReview.chart.showLastYear')}
          </label>
        </div>
      </div>

      {hasActiveFilters ? (
        <div className="mb-3 flex flex-wrap items-center gap-2 rounded-md border border-blue-100 bg-blue-50/60 px-3 py-2">
          <span className="text-xs font-medium text-gray-600">{t('forecastReview.chart.activeFilters')}</span>
          <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1.5">
            {activeFilters.map((filter) => (
              <span
                key={`${filter.id}-${filter.value}`}
                className="inline-flex max-w-full items-center gap-1 rounded-full border border-blue-200 bg-white py-0.5 pl-2.5 pr-1 text-xs text-gray-800"
              >
                <span className="truncate">{filter.label}</span>
                {onClearFilter ? (
                  <button
                    type="button"
                    onClick={() => onClearFilter(filter.id)}
                    className="inline-flex shrink-0 rounded-full p-0.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
                    aria-label={t('forecastReview.chart.removeFilter', { label: filter.label })}
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                ) : null}
              </span>
            ))}
          </div>
          {onClearAllFilters ? (
            <button
              type="button"
              onClick={onClearAllFilters}
              className="shrink-0 text-xs font-medium text-[#1681ba] hover:text-[#126a9a]"
            >
              {t('forecastReview.chart.clearAllFilters')}
            </button>
          ) : null}
        </div>
      ) : null}

      <div className="relative h-[320px]">
        {loading ? (
          <div className="flex h-full items-center justify-center text-sm text-gray-500">
            {t('forecastReview.chart.loading')}
          </div>
        ) : labels.length === 0 ? (
          <div className="flex h-full flex-col items-center justify-center text-sm text-gray-500">
            <p>{t('forecastReview.chart.noData')}</p>
            <p className="mt-1 text-xs text-gray-400">{t('forecastReview.chart.noDataHint')}</p>
          </div>
        ) : (
          <div className={isWeekly ? 'h-full overflow-x-auto' : 'h-full'}>
            <div className="h-full" style={chartMinWidth ? { minWidth: chartMinWidth } : undefined}>
              <Line data={chartData} options={options} />
            </div>
          </div>
        )}
      </div>
      {data?.forecast_start_period ? (
        <p className="mt-2 text-xs text-gray-500">
          {t('forecastReview.chart.forecastStarts', { period: formatPeriod(data.forecast_start_period, granularity) })}
          {showLastYear && sortedPeriods.length > 0 ? (
            <span className="ml-2 text-gray-400">
              · {t('forecastReview.chart.lastYearHint')}
            </span>
          ) : null}
        </p>
      ) : null}
    </div>
  );
}
